import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Shuffle, ArrowLeft, Loader2, Globe2, MapPin, ArrowRight } from "lucide-react";
import { DateFlexPicker } from "@/components/experiences/DateFlexPicker";
import type { DateSelection } from "@/components/experiences/DateFlexPicker";
import type { CatalogCity } from "@/lib/cities/city_catalog";
import { getHomeFeaturedDestinations, pickSurpriseDestination } from "@/lib/cities/home_featured";
import { HOME_HERO_PLACEHOLDERS } from "@/lib/experiences/homeSearchPlaceholders";
import { useRheaLoading, waitForNextPaint } from "@/lib/ui/RheaLoadingContext";
import { SiteFooter } from "@/components/SiteFooter";
import { RheaPrototypeHeader } from "@/components/RheaPrototypeHeader";
import {
  DestinationPicker,
  type SelectedDestination,
} from "@/components/experiences/DestinationPicker";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  exploreDestinations,
  type DestinationExploreResult,
  type DestinationCoverageCountry,
} from "@/lib/api";
import { MarketplaceShell } from "@/components/experiences/marketplace/MarketplaceShell";
import {
  marketplaceSearchContextKey,
  type MarketplaceSearchContext,
} from "@/lib/experiences/marketplaceSearchContext";
import { parseQuery } from "@/lib/experiences/queryParser";
import { track } from "@/lib/analytics";
import { logMarketplaceEvent, initPageEngagementTracking } from "@/lib/marketplaceEventLog";
import { useI18n, translate } from "@/lib/i18n/useI18n";
import type { MessageKey } from "@/lib/i18n/messages";
import type { Language } from "@/lib/i18n/languages";

// ─── Unified Marketplace Shell + Direct Structured Search V1 ────────────────
// Home hosts the real marketplace inline for a structured city/airport
// selection — never navigating to /experiences, never calling /parse/intent
// or /experiences/sequence first. See useHomeSearchParams below for the URL
// schema and buildHomeSearchContext for the MarketplaceSearchContext bridge.

function safeHomeString(value: unknown, maxLength: number): string | undefined {
  if (typeof value !== "string") return undefined;
  const normalized = value.trim();
  return normalized.length > 0 && normalized.length <= maxLength ? normalized : undefined;
}
function safeHomeInt(value: unknown, min: number, max: number): number | undefined {
  if (typeof value !== "string" && typeof value !== "number") return undefined;
  const parsed = Number(value);
  return Number.isInteger(parsed) && parsed >= min && parsed <= max ? parsed : undefined;
}
function safeHomeIata(value: unknown): string | undefined {
  const s = safeHomeString(value, 4);
  return s && /^[A-Z]{3}$/.test(s.toUpperCase()) ? s.toUpperCase() : undefined;
}

export interface HomeSearchParams {
  destination_iata?:   string;
  destination_name?:   string;
  q?:                  string;
  departure_date?:     string;
  return_date?:        string;
  travel_month?:       string;
  flexible_days?:      number;
  flexibility_window?: number;
  adults?:             number;
  children_ages?:      string;
  /** Country/region/continent geographic-coverage mode — never a city IATA. */
  scope?:              string;
}

// ─── Route ────────────────────────────────────────────────────────────────────

export const Route = createFileRoute("/")({
  component: LandingPage,
  validateSearch: (search: Record<string, unknown>): HomeSearchParams => ({
    destination_iata:   safeHomeIata(search.destination_iata),
    destination_name:   safeHomeString(search.destination_name, 100),
    q:                  safeHomeString(search.q, 300),
    departure_date:     safeHomeString(search.departure_date, 10),
    return_date:        safeHomeString(search.return_date, 10),
    travel_month:       safeHomeString(search.travel_month, 10),
    flexible_days:      safeHomeInt(search.flexible_days, 1, 90),
    flexibility_window: safeHomeInt(search.flexibility_window, 0, 30),
    adults:             safeHomeInt(search.adults, 1, 10),
    children_ages:      safeHomeString(search.children_ages, 80),
    scope:              safeHomeString(search.scope, 120),
  }),
  head: () => ({
    meta: [
      { title: "Sift Trip — Inteligencia de viaje" },
      {
        name: "description",
        content: "Descubre experiencias, actividades y excursiones reales para tu próximo viaje.",
      },
    ],
  }),
});

// ─── Design tokens ────────────────────────────────────────────────────────────

const T = {
  navy:       "oklch(0.120 0.040 258)",
  navyMid:    "oklch(0.170 0.055 255)",
  blue:       "oklch(0.580 0.220 256)",
  blueFaint:  "oklch(0.580 0.220 256 / 0.10)",
  white:      "oklch(1 0 0)",
  whiteMid:   "oklch(1 0 0 / 0.60)",
  whiteMuted: "oklch(1 0 0 / 0.30)",
  ivory:      "oklch(0.988 0.003 240)",
  cream:      "oklch(0.988 0.003 240)",
  parchment:  "oklch(0.993 0.004 72)",
  ink:        "oklch(0.07 0.012 258)",
  inkMuted:   "oklch(0.38 0.016 258 / 0.52)",
  border:     "oklch(0.880 0.014 70 / 0.50)",
  borderSoft: "oklch(0.880 0.014 70 / 0.28)",
} as const;

const EASE = [0.22, 1, 0.36, 1] as const;

// ─── Rotating search placeholders ────────────────────────────────────────────
// Home/Search Portfolio Alignment V1 — placeholders live in
// lib/experiences/homeSearchPlaceholders.ts, the single source both Home and
// the Experiences search bar read from (same launch_promoted + single-Madrid-
// exception policy as HOME_FEATURED_IATAS, marketplace-framed copy, each
// entry verified against parseQuery() in homeSearchPlaceholders.test.ts).

const PLACEHOLDERS = HOME_HERO_PLACEHOLDERS;

// ─── Traveler types ───────────────────────────────────────────────────────────

type TravelerType = "solo" | "pareja" | "amigos" | "familia" | "mayores";

interface TravelerOption {
  value:        TravelerType;
  label:        string;
  adultDefault: number;
}

const TRAVELER_OPTIONS: TravelerOption[] = [
  { value: "solo",    label: "Solo",            adultDefault: 1 },
  { value: "pareja",  label: "Pareja",          adultDefault: 2 },
  { value: "amigos",  label: "Amigos",          adultDefault: 3 },
  { value: "familia", label: "Familia",         adultDefault: 2 },
  { value: "mayores", label: "Adultos mayores", adultDefault: 2 },
];

// Sprint: Frontend UI i18n Foundation V1, Parte B — TRAVELER_OPTIONS.label
// stays the Spanish default (still read directly by travelerChipLabel()'s
// own backward-compatible single-arg callers/tests below), but any JSX
// that renders an option to the user should look up its translated label
// here instead of `opt.label` directly.
const TRAVELER_LABEL_KEYS: Record<TravelerType, MessageKey> = {
  solo: "home.travelers.solo",
  pareja: "home.travelers.couple",
  amigos: "home.travelers.friends",
  familia: "home.travelers.family",
  mayores: "home.travelers.seniors",
};

export interface TravelerState {
  adults:       number;
  childrenAges: number[];
  type:         TravelerType;
  /**
   * Sprint: Search Scope + Locale-Stable Inventory + Traveler Type Label
   * Hotfix V1, Parte C — true once there is a REAL signal about traveler
   * type (an explicit popover pick OR a phrase detected in the query,
   * e.g. "en pareja"), as opposed to the untouched initial default. Root
   * cause this fixes: DEFAULT_TRAVELER's own shape (2 adults, no
   * children, type="pareja") is numerically IDENTICAL to what a user
   * gets by explicitly picking "Pareja" from the popover — isDefaultTraveler()
   * used to treat both as the same "show a generic label" case, so an
   * explicit choice displayed as "Viajeros" exactly like a fresh,
   * untouched page load. Client-side UI state only — never serialized to
   * the URL or sent to the backend (adults/childrenAges keep doing that
   * job unchanged), so this never widens the wire contract.
   */
  manuallySet?: boolean;
  /**
   * True only for an explicit popover interaction (never a query-text
   * detection) — the query-driven effect below checks this before ever
   * overwriting `type`, so a user's own deliberate click always outranks
   * a later, looser phrase detected while they keep typing.
   */
  explicitlyChosen?: boolean;
}

export const DEFAULT_TRAVELER: TravelerState = { adults: 2, childrenAges: [], type: "pareja" };

function isDefaultTraveler(s: TravelerState): boolean {
  return !s.manuallySet && s.adults === 2 && s.childrenAges.length === 0 && s.type === "pareja";
}

// Sprint: Frontend UI i18n Foundation V1, Parte B — `language` is an
// OPTIONAL second parameter defaulting to "es", so every EXISTING single-
// arg call site/test (see routes/-homeUrlRestoration.test.ts) keeps
// returning byte-for-byte the same Spanish string it always did; only a
// caller that explicitly passes a language gets translated copy. Adult/
// child counts use a small local singular/plural word choice (never a
// full ICU plural-rules dependency — this sprint's own "no librería
// pesada" restriction) rather than a MESSAGES key, since the count itself
// is the variable part.
export function travelerChipLabel(s: TravelerState, language: Language = "es"): string {
  if (isDefaultTraveler(s)) return translate(language, "home.travelers.defaultLabel");
  const adultWord = adultWordFor(s.adults, language);
  const a = `${s.adults} ${adultWord}`;
  if (s.childrenAges.length > 0) {
    const n = s.childrenAges.length;
    return `${a} · ${n} ${childWordFor(n, language)}`;
  }
  if (s.type === "solo") return translate(language, "home.travelers.solo");
  if (s.type === "mayores") return translate(language, "home.travelers.seniors");
  // Sprint: Search Scope + Locale-Stable Inventory + Traveler Type Label
  // Hotfix V1, Parte C — "pareja"/"amigos" used to fall all the way
  // through to the bare adult count ("2 adultos"), silently dropping the
  // one piece of intention the user actually stated. Shows the type's own
  // label alone when the adult count matches that type's own default
  // (e.g. "Pareja" for 2 adults), or qualified with the real count
  // otherwise (e.g. "Pareja · 3 adultos") — never invented, always
  // derived from the same TRAVELER_OPTIONS the popover itself renders.
  const opt = TRAVELER_OPTIONS.find(o => o.value === s.type);
  if (opt) {
    const label = translate(language, TRAVELER_LABEL_KEYS[opt.value]);
    return s.adults === opt.adultDefault ? label : `${label} · ${a}`;
  }
  return a;
}

function adultWordFor(count: number, language: Language): string {
  if (language === "en") return count === 1 ? "adult" : "adults";
  if (language === "pt") return count === 1 ? "adulto" : "adultos";
  return `adulto${count !== 1 ? "s" : ""}`;
}

function childWordFor(count: number, language: Language): string {
  if (language === "en") return count === 1 ? "child" : "children";
  if (language === "pt") return count === 1 ? "criança" : "crianças";
  return `niño${count !== 1 ? "s" : ""}`;
}

// ─── Sprint: Search Scope + Locale-Stable Inventory + Traveler Type Label
// Hotfix V1, Parte C — generic, keyword-based traveler-type detection
// from the SAME free-text query field deriveMarketplaceInterests() already
// parses. Deliberately a small, local table (not lib/intent/taxonomy.ts's
// own richer IntentProfile.travelerType — that module models a different,
// larger vocabulary for the AI-guided itinerary flow and isn't wired into
// Home's lightweight query→interests pipeline at all) so this hotfix's
// blast radius stays inside routes/index.tsx's own existing traveler
// contract.
const TRAVELER_TYPE_QUERY_ALIASES: Record<TravelerType, string[]> = {
  solo:    ["solo", "sola", "yo solo", "yo sola"],
  pareja:  ["en pareja", "pareja", "novios", "luna de miel"],
  amigos:  ["amigos", "amigas", "grupo de amigos"],
  familia: ["en familia", "familia", "con ninos", "con hijos"],
  mayores: ["adultos mayores", "tercera edad"],
};

function foldForTravelerMatch(text: string): string {
  return text.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();
}

/**
 * Returns the first TravelerType whose alias phrase appears in `text`, or
 * null when none match. Pure, accent/case-insensitive, never throws.
 */
export function matchTravelerTypeFromQuery(text: string): TravelerType | null {
  const folded = foldForTravelerMatch(text);
  for (const type of Object.keys(TRAVELER_TYPE_QUERY_ALIASES) as TravelerType[]) {
    if (TRAVELER_TYPE_QUERY_ALIASES[type].some(alias => folded.includes(foldForTravelerMatch(alias)))) {
      return type;
    }
  }
  return null;
}

// ─── SVG icons ────────────────────────────────────────────────────────────────
function SearchIconSm() {
  return (
    <svg width="15" height="15" viewBox="0 0 15 15" fill="none" aria-hidden>
      <circle cx="6.5" cy="6.5" r="4.5" stroke="currentColor" strokeWidth="1.5" />
      <path d="M10 10 13 13" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}
function UsersIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 13 13" fill="none" aria-hidden>
      <circle cx="5" cy="4" r="2" stroke="currentColor" strokeWidth="1.3" />
      <path d="M1 12c0-2.2 1.8-4 4-4s4 1.8 4 4" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
      <circle cx="9.5" cy="3.5" r="1.5" stroke="currentColor" strokeWidth="1.2" />
      <path d="M11 11c0-1.7-1-2.8-2.5-3.2" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
    </svg>
  );
}
function PlusIcon() {
  return (
    <svg width="11" height="11" viewBox="0 0 11 11" fill="none" aria-hidden>
      <path d="M5.5 1v9M1 5.5h9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}
function MinusIcon() {
  return (
    <svg width="11" height="11" viewBox="0 0 11 11" fill="none" aria-hidden>
      <path d="M1 5.5h9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}
function XIcon() {
  return (
    <svg width="9" height="9" viewBox="0 0 9 9" fill="none" aria-hidden>
      <path d="M1.5 1.5l6 6M7.5 1.5l-6 6" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
    </svg>
  );
}

// ─── TravelerPopover ──────────────────────────────────────────────────────────

function TravelerPopover({
  state,
  onChange,
}: {
  state:    TravelerState;
  onChange: (s: TravelerState) => void;
}) {
  const { adults, childrenAges, type } = state;
  const { language, t } = useI18n();

  function setAdults(n: number) {
    onChange({ ...state, adults: Math.max(1, n), manuallySet: true, explicitlyChosen: true });
  }
  function setType(t: TravelerType) {
    const opt = TRAVELER_OPTIONS.find(o => o.value === t)!;
    const nextAdults   = t === "solo" ? 1 : Math.max(adults, opt.adultDefault);
    const nextChildren = t === "solo" ? [] : childrenAges;
    onChange({ adults: nextAdults, childrenAges: nextChildren, type: t, manuallySet: true, explicitlyChosen: true });
  }
  function addChild() {
    onChange({ ...state, childrenAges: [...childrenAges, 8], type: "familia", manuallySet: true, explicitlyChosen: true });
  }
  function removeChild(i: number) {
    const next = childrenAges.filter((_, idx) => idx !== i);
    onChange({
      ...state,
      childrenAges: next,
      type: next.length === 0 && type === "familia" ? "pareja" : type,
      manuallySet: true, explicitlyChosen: true,
    });
  }
  function setChildAge(i: number, age: number) {
    onChange({
      ...state,
      childrenAges: childrenAges.map((a, idx) => idx === i ? age : a),
      manuallySet: true, explicitlyChosen: true,
    });
  }

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-[13px] font-medium transition-colors"
          style={{
            background: "#F5F5F5",
            border:     "1px solid #EAEAEA",
            color:      "#5A5A5A",
          }}
        >
          <UsersIcon />
          <span>{travelerChipLabel(state, language)}</span>
          <svg width="10" height="10" viewBox="0 0 10 10" fill="none" aria-hidden><path d="M2.5 3.5l2.5 3 2.5-3" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" /></svg>
        </button>
      </PopoverTrigger>

      <PopoverContent
        align="start"
        sideOffset={8}
        className="p-4 w-72 rounded-2xl shadow-xl"
        style={{ background: T.parchment, border: `1px solid ${T.border}`, zIndex: 60 }}
      >
        <p className="text-[10px] font-bold tracking-widest uppercase mb-3" style={{ color: T.inkMuted }}>
          {t("home.travelers.popoverHeading")}
        </p>
        <div className="flex flex-wrap gap-1.5 mb-4">
          {TRAVELER_OPTIONS.map(opt => (
            <button
              key={opt.value}
              type="button"
              onClick={() => setType(opt.value)}
              className="px-3 py-1.5 rounded-xl text-xs font-semibold transition-all"
              style={
                type === opt.value
                  ? { background: "#E8194B", color: "#FFFFFF" }
                  : { background: "oklch(0.930 0.010 70)", color: T.ink }
              }
            >
              {t(TRAVELER_LABEL_KEYS[opt.value])}
            </button>
          ))}
        </div>

        <div className="flex items-center justify-between mb-3">
          <div>
            <p className="text-sm font-semibold" style={{ color: T.ink }}>{t("home.travelers.adults")}</p>
            <p className="text-[11px]" style={{ color: T.inkMuted }}>{t("home.travelers.adultsAgeNote")}</p>
          </div>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => setAdults(adults - 1)}
              disabled={adults <= 1}
              className="w-7 h-7 rounded-full flex items-center justify-center transition-colors disabled:opacity-30"
              style={{ background: "oklch(0.930 0.010 70)", color: T.ink }}
              aria-label={t("home.travelers.reduceAdultsAria")}
            >
              <MinusIcon />
            </button>
            <span className="w-5 text-center text-sm font-semibold" style={{ color: T.ink }}>
              {adults}
            </span>
            <button
              type="button"
              onClick={() => setAdults(adults + 1)}
              className="w-7 h-7 rounded-full flex items-center justify-center transition-colors"
              style={{ background: "oklch(0.930 0.010 70)", color: T.ink }}
              aria-label={t("home.travelers.addAdultAria")}
            >
              <PlusIcon />
            </button>
          </div>
        </div>

        {childrenAges.length > 0 && (
          <div className="mb-3 space-y-2">
            <p className="text-[10px] font-bold tracking-widest uppercase" style={{ color: T.inkMuted }}>
              {t("home.travelers.children")}
            </p>
            {childrenAges.map((age, i) => (
              <div key={i} className="flex items-center gap-2">
                <label className="text-xs flex-1" style={{ color: T.ink }}>{t("home.travelers.childLabel", { n: i + 1 })}</label>
                <select
                  value={age}
                  onChange={e => setChildAge(i, Number(e.target.value))}
                  className="rounded-lg px-2 py-1 text-xs"
                  style={{ background: "oklch(0.930 0.010 70)", border: `1px solid ${T.border}`, color: T.ink }}
                  aria-label={t("home.travelers.childAgeAria", { n: i + 1 })}
                >
                  {Array.from({ length: 18 }, (_, n) => (
                    <option key={n} value={n}>{t("home.travelers.ageYearsOption", { n })}</option>
                  ))}
                </select>
                <button
                  type="button"
                  onClick={() => removeChild(i)}
                  className="w-6 h-6 rounded-full flex items-center justify-center"
                  style={{ background: "oklch(0.900 0.010 70)", color: T.inkMuted }}
                  aria-label={t("home.travelers.removeChildAria", { n: i + 1 })}
                >
                  <XIcon />
                </button>
              </div>
            ))}
          </div>
        )}

        <button
          type="button"
          onClick={addChild}
          className="flex items-center gap-1.5 text-xs font-semibold mt-1"
          style={{ color: T.blue }}
        >
          <PlusIcon />
          {t("home.travelers.addChild")}
        </button>
      </PopoverContent>
    </Popover>
  );
}

// ─── HomeHero ─────────────────────────────────────────────────────────────────
// Single coherent search surface: input row + controls row, 8px radius.
// No decorative orbs or floating blobs. Navy base, ivory and gold accents.

interface HomeHeroProps {
  destinationValue:    string;
  onDestinationChange: (value: string) => void;
  selectedDestination: SelectedDestination | null;
  onDestinationSelect: (destination: SelectedDestination | null) => void;
  query:            string;
  onQueryChange:    (q: string) => void;
  onSubmit:         () => void;
  loading:          boolean;
  dateSelection:    DateSelection;
  onDateChange:     (d: DateSelection) => void;
  traveler:         TravelerState;
  onTravelerChange: (s: TravelerState) => void;
  placeholderIdx:   number;
}

function HomeHero({
  destinationValue, onDestinationChange,
  selectedDestination, onDestinationSelect,
  query, onQueryChange, onSubmit,
  loading, dateSelection, onDateChange,
  traveler, onTravelerChange,
  placeholderIdx,
}: HomeHeroProps) {
  const reduce = useReducedMotion();
  const { t } = useI18n();

  const fadeUp = (delay: number) => ({
    initial:    reduce ? false : { opacity: 0, y: 12 },
    animate:    { opacity: 1, y: 0 },
    transition: reduce
      ? { duration: 0 }
      : { duration: 0.48, delay, ease: EASE },
  });

  const canSubmit = selectedDestination !== null;

  return (
    <section
      aria-label={t("home.search.sectionAriaLabel")}
      style={{
        // Marketplace Grid Redesign + Provider Badges + Practical Filters
        // V1 — light hero (restriction: "no volver al hero oscuro
        // actual"). #FAFAFA per this sprint's own palette spec.
        background:    "#FAFAFA",
        paddingTop:    "clamp(1.75rem, 4.5vw, 2.75rem)",
        paddingBottom: "clamp(1.5rem, 3.5vw, 2.25rem)",
        position:      "relative",
      }}
    >
      <div
        className="absolute top-0 left-0 right-0 h-px"
        aria-hidden
        style={{ background: "#EAEAEA" }}
      />

      <div
        className="relative z-10 px-5 sm:px-8 mx-auto"
        style={{ maxWidth: "1100px" }}
      >
        {/* Brand heading */}
        <motion.div {...fadeUp(0)} className="mb-1">
          <span
            className="text-[10px] font-semibold tracking-[0.22em] uppercase"
            style={{ color: "#E8194B" }}
          >
            {t("home.hero.eyebrow")}
          </span>
        </motion.div>

        <motion.h1
          {...fadeUp(0.06)}
          className="font-sans font-semibold leading-tight mb-2"
          style={{
            fontSize:      "clamp(1.9rem, 4.4vw, 3rem)",
            letterSpacing: "-0.01em",
            color:         "#111111",
          }}
        >
          {t("home.hero.headline")}
        </motion.h1>

        <motion.p
          {...fadeUp(0.13)}
          className="text-sm sm:text-[15px] leading-relaxed mb-6"
          style={{ color: "#5A5A5A", maxWidth: "480px" }}
        >
          {t("home.hero.subcopy")}
        </motion.p>

        {/* ── Unified search surface ── */}
        <motion.div {...fadeUp(0.20)}>
          <div
            style={{
              borderRadius: "8px",
              background:   "#FFFFFF",
              border:       "1.5px solid #EAEAEA",
              boxShadow:    "0 4px 24px rgba(0,0,0,0.06)",
            }}
          >
            {/* Row 1: structured geographic identity */}
            <div className="flex items-center gap-3 px-4 py-3.5">
              <DestinationPicker
                value={destinationValue}
                onValueChange={onDestinationChange}
                selection={selectedDestination}
                onSelect={onDestinationSelect}
                disabled={loading}
                dark={false}
              />
            </div>

            <div
              style={{
                borderTop: "1px solid #EAEAEA",
                marginLeft: "1rem",
                marginRight: "1rem",
              }}
            />

            {/* Row 2: optional travel intent, never destination identity */}
            <div className="flex items-center gap-3 px-4 py-3.5">
              <span style={{ color: "#5A5A5A", flexShrink: 0 }}>
                <SearchIconSm />
              </span>
              <input
                type="text"
                value={query}
                onChange={e => onQueryChange(e.target.value)}
                onKeyDown={e => { if (e.key === "Enter") onSubmit(); }}
                placeholder={PLACEHOLDERS[placeholderIdx]}
                className="flex-1 bg-transparent text-sm outline-none placeholder:text-black/30"
                style={{ color: "#111111" }}
                aria-label={t("home.search.ariaLabel")}
                autoComplete="off"
              />
              {query && (
                <button
                  type="button"
                  onClick={() => onQueryChange("")}
                  className="shrink-0 transition-opacity hover:opacity-70"
                  style={{ color: "#5A5A5A" }}
                  aria-label={t("home.search.clearAriaLabel")}
                >
                  <XIcon />
                </button>
              )}
            </div>

            {/* Separator */}
            <div
              style={{
                borderTop: "1px solid #EAEAEA",
                marginLeft: "1rem",
                marginRight: "1rem",
              }}
            />

            {/* Row 3 mobile: 2-col grid (Date | Travelers), full-width CTA below */}
            <div className="sm:hidden grid grid-cols-2 gap-2 px-4 pb-3 pt-1">
              <DateFlexPicker value={dateSelection} onChange={onDateChange} compact lightMode />
              <TravelerPopover state={traveler} onChange={onTravelerChange} />
              <button
                type="button"
                onClick={onSubmit}
                disabled={loading || !canSubmit}
                className="col-span-2 w-full flex items-center justify-center gap-2 py-2.5 text-sm font-bold transition-all duration-200 disabled:opacity-40 disabled:cursor-not-allowed focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2"
                style={{
                  background:   canSubmit && !loading ? "#E8194B" : "#F5F5F5",
                  color:        canSubmit && !loading ? "#FFFFFF" : "#5A5A5A",
                  borderRadius: "6px",
                  outlineColor: "#E8194B",
                }}
              >
                {loading ? t("home.search.buttonLoading") : t("home.search.buttonIdle")}
              </button>
            </div>

            {/* Row 3 desktop: horizontal flex */}
            <div className="hidden sm:flex items-center gap-2 px-4 py-3">
              <DateFlexPicker value={dateSelection} onChange={onDateChange} compact lightMode />
              <TravelerPopover state={traveler} onChange={onTravelerChange} />
              <div className="flex-1" />
              <button
                type="button"
                onClick={onSubmit}
                disabled={loading || !canSubmit}
                className="flex items-center gap-2 px-5 py-2.5 text-sm font-bold transition-all duration-200 disabled:opacity-40 disabled:cursor-not-allowed focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2"
                style={{
                  background:   canSubmit && !loading ? "#E8194B" : "#F5F5F5",
                  color:        canSubmit && !loading ? "#FFFFFF" : "#5A5A5A",
                  borderRadius: "6px",
                  outlineColor: "#E8194B",
                }}
              >
                {loading ? t("home.search.buttonLoading") : t("home.search.buttonIdle")}
              </button>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

// ─── TrustBand ────────────────────────────────────────────────────────────────
// Compact horizontal strip with three verifiable product statements.
// No invented metrics, no false claims.

function TrustBand() {
  // Marketplace Grid Redesign + Provider Badges + Practical Filters V1 —
  // exact copy from this sprint's own brief. Clean, not an apology: no
  // circular "seal" graphic (deliberately omitted — the brief marks it
  // optional and asks the visual not to lean on it), just three plain
  // verifiable statements with a small magenta marker each.
  const { t } = useI18n();
  const items = [
    {
      label:  t("home.trust.direct.title"),
      detail: t("home.trust.direct.subtitle"),
    },
    {
      label:  t("home.trust.transparent.title"),
      detail: t("home.trust.transparent.subtitle"),
    },
    {
      label:  t("home.trust.neutral.title"),
      detail: t("home.trust.neutral.subtitle"),
    },
  ];

  return (
    <div
      style={{
        borderBottom: "1px solid #EAEAEA",
        background:   "#FFFFFF",
      }}
    >
      <div
        className="mx-auto px-5 sm:px-8 py-4 sm:py-5"
        style={{ maxWidth: "1100px" }}
      >
        <div className="flex flex-col sm:flex-row gap-5 sm:gap-0">
          {items.map((item, i) => (
            <div
              key={i}
              className={`flex items-start gap-2.5 flex-1 ${i > 0 ? "sm:pl-8 sm:border-l" : ""}`}
              style={{ borderColor: "#EAEAEA" }}
            >
              <span
                aria-hidden
                style={{ color: "#E8194B", flexShrink: 0, marginTop: "5px", fontSize: "10px" }}
              >
                ●
              </span>
              <div>
                <p className="text-xs font-semibold leading-snug" style={{ color: "#111111" }}>
                  {item.label}
                </p>
                <p className="text-[11px] mt-0.5 leading-relaxed" style={{ color: "#5A5A5A" }}>
                  {item.detail}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── CityCard ─────────────────────────────────────────────────────────────────

function CityCard({
  city,
  size = "normal",
  onSelect,
}: {
  city:     CatalogCity;
  size?:    "large" | "normal";
  onSelect: (display: string, iata: string) => void;
}) {
  const [imgError, setImgError] = useState(false);
  const [hovered, setHovered]   = useState(false);
  const reduce                  = useReducedMotion();

  return (
    <motion.button
      type="button"
      onClick={() => onSelect(city.display, city.iata)}
      className="group relative flex flex-col overflow-hidden rounded-2xl text-left w-full focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2"
      style={{
        minHeight:    size === "large" ? "220px" : "170px",
        background:   !city.photo || imgError ? city.gradient : undefined,
        boxShadow:    hovered && !reduce
          ? "0 10px 28px oklch(0.07 0.012 258 / 0.16), 0 0 0 1px oklch(0.07 0.012 258 / 0.08)"
          : "0 2px 10px oklch(0.07 0.012 258 / 0.07), 0 0 0 1px oklch(0.07 0.012 258 / 0.05)",
        transition:   "box-shadow 0.28s ease",
        outlineColor: "#E8194B",
      }}
      whileHover={reduce ? {} : { y: -3 }}
      transition={{ duration: 0.28, ease: EASE }}
      onHoverStart={() => setHovered(true)}
      onHoverEnd={() => setHovered(false)}
      aria-label={`Explorar ${city.display}`}
    >
      {city.photo && !imgError && (
        <>
          <img
            src={city.photo}
            alt={city.display}
            className="absolute inset-0 w-full h-full object-cover"
            style={{
              transition: reduce ? undefined : "transform 0.50s cubic-bezier(0.22,1,0.36,1)",
              transform:  hovered && !reduce ? "scale(1.04)" : "scale(1)",
            }}
            onError={() => setImgError(true)}
          />
          <div
            className="absolute inset-0"
            style={{
              background: "linear-gradient(to top, oklch(0.07 0.012 258 / 0.78) 0%, oklch(0.07 0.012 258 / 0.08) 55%, transparent 100%)",
              transition: reduce ? undefined : "opacity 0.25s ease",
              opacity:    hovered && !reduce ? 1 : 0.86,
            }}
          />
        </>
      )}
      {(!city.photo || imgError) && (
        <div
          className="absolute inset-0"
          style={{
            background: `linear-gradient(160deg, color-mix(in oklch, ${city.accentColor} 14%, transparent) 0%, color-mix(in oklch, ${city.accentColor} 6%, transparent) 100%)`,
          }}
        />
      )}

      <div className="relative z-10 flex flex-col h-full p-4">
        <div className="flex flex-wrap gap-1 mb-auto">
          {city.vibes.slice(0, 2).map(v => (
            <span
              key={v}
              className="text-[9px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded-full"
              style={{
                background: "oklch(1 0 0 / 0.14)",
                color:      city.photo && !imgError ? T.white : city.accentColor,
              }}
            >
              {v}
            </span>
          ))}
        </div>

        <div className="mt-3">
          <p
            className="text-lg font-bold leading-tight"
            style={{ color: city.photo && !imgError ? T.white : T.ink }}
          >
            {city.display}
          </p>
          <p
            className="text-xs mt-0.5"
            style={{ color: city.photo && !imgError ? "oklch(1 0 0 / 0.58)" : T.inkMuted }}
          >
            {city.country}
          </p>
        </div>
      </div>

      {/* Marketplace Visual Polish — Loading, Brand Color Cleanup &
          Provider Logos V1, Parte B — magenta accent arrow, appears on
          hover (previously gold). */}
      <div
        className="absolute bottom-4 right-4 w-7 h-7 rounded-full flex items-center justify-center pointer-events-none"
        style={{
          background: "#E8194B",
          opacity:    hovered && !reduce ? 1 : 0,
          transform:  hovered && !reduce ? "translateX(0)" : "translateX(5px)",
          transition: "opacity 0.20s ease, transform 0.20s ease",
        }}
        aria-hidden
      >
        <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
          <path d="M2 5h6M5 2l3 3-3 3" stroke="#FFFFFF" strokeWidth="1.5"
            strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </div>
    </motion.button>
  );
}

// ─── HomeSuggestions ──────────────────────────────────────────────────────────

function HomeSuggestions({ onSelectCity }: { onSelectCity: (display: string, iata: string) => void }) {
  const cities = getHomeFeaturedDestinations().slice(0, 12);
  const reduce = useReducedMotion();
  const { t } = useI18n();

  function handleSurprise() {
    const city = pickSurpriseDestination();
    if (!city) return;
    onSelectCity(city.display, city.iata);
  }

  return (
    <section className="px-5 sm:px-8 py-10 sm:py-12">
      <div style={{ maxWidth: "1040px", margin: "0 auto" }}>

        <div className="flex items-end justify-between mb-6">
          <div>
            <p
              className="text-[10px] font-bold tracking-[0.18em] uppercase mb-1"
              style={{ color: T.inkMuted }}
            >
              {t("home.featured.heading")}
            </p>
            <h2
              className="text-xl sm:text-2xl font-semibold leading-tight"
              style={{ color: T.ink, letterSpacing: "-0.01em" }}
            >
              {t("home.featured.inspireHeading")}
            </h2>
          </div>

          {/* "Sorpréndeme" replaces "Explorar todo" */}
          <button
            type="button"
            onClick={handleSurprise}
            className="flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full transition-all duration-200 hover:opacity-80 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2"
            style={{
              background:   "#E8194B1A",
              border:       "1px solid #E8194B4D",
              color:        "#E8194B",
              outlineColor: "#E8194B",
            }}
            aria-label={t("home.featured.surpriseMeAria")}
          >
            <Shuffle size={12} aria-hidden />
            {t("home.featured.surpriseMe")}
          </button>
        </div>

        {/* Mobile: horizontal scroll carousel */}
        <div
          className="sm:hidden flex gap-3 pb-3 -mx-5 px-5"
          style={{
            overflowX:                "auto",
            scrollSnapType:           "x mandatory",
            scrollbarWidth:           "none",
            WebkitOverflowScrolling:  "touch",
          } as React.CSSProperties}
        >
          {cities.map((city) => (
            <div
              key={city.iata}
              style={{
                minWidth:        "72vw",
                scrollSnapAlign: "start",
                flexShrink:      0,
              }}
            >
              <CityCard city={city} onSelect={onSelectCity} />
            </div>
          ))}
          {/* Trailing spacer so last card doesn't snap to edge */}
          <div style={{ minWidth: "1.25rem", flexShrink: 0 }} aria-hidden />
        </div>

        {/* Desktop: asymmetric grid */}
        <div className="hidden sm:grid sm:grid-cols-3 lg:grid-cols-4 gap-3">
          {cities.map((city, i) => (
            <motion.div
              key={city.iata}
              className={
                i === 0 ? "col-span-2" :
                i === 4 ? "col-span-2" :
                "col-span-1"
              }
              initial={reduce ? false : { opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={reduce
                ? { duration: 0 }
                : { duration: 0.48, delay: i * 0.05, ease: EASE }
              }
            >
              <CityCard
                city={city}
                size={i === 0 || i === 4 ? "large" : "normal"}
                onSelect={onSelectCity}
              />
            </motion.div>
          ))}
        </div>

      </div>
    </section>
  );
}

// ─── CompactSearchBar ─────────────────────────────────────────────────────────
// Unified Marketplace Shell + Direct Structured Search V1, action 9 — the
// full hero shrinks to this single slim bar once a structured search or a
// geographic coverage view is active, so the dense marketplace results get
// the vertical space instead of the full editorial hero.

function CompactSearchBar({
  label,
  onEdit,
}: {
  label: string;
  onEdit: () => void;
}) {
  const { t } = useI18n();
  return (
    <section
      aria-label={t("home.compactSearch.ariaLabel")}
      className="flex items-center gap-3 px-5 sm:px-8 py-3"
      style={{ background: "#FAFAFA", borderBottom: "1px solid #EAEAEA" }}
    >
      <button
        type="button"
        onClick={onEdit}
        className="inline-flex items-center gap-1.5 text-[13px] font-semibold shrink-0 transition-opacity hover:opacity-80 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2"
        style={{ color: "#5A5A5A", outlineColor: "#E8194B" }}
      >
        <ArrowLeft size={14} aria-hidden />
        {t("marketplace.toolbar.newSearch")}
      </button>
      <span className="h-4 w-px shrink-0" style={{ background: "#EAEAEA" }} aria-hidden />
      <span className="text-[13px] font-semibold truncate" style={{ color: "#111111" }}>
        {label}
      </span>
    </section>
  );
}

// ─── HomeGeographicCoverage ─────────────────────────────────────────────────
// Unified Marketplace Shell + Direct Structured Search V1, action 6 —
// country/region/continent coverage rendered inline in the same shell,
// never navigating away. Never associates the selected scope (a country,
// region, or continent) with any single city's IATA — only each individual
// city entry ever shows its own IATA, exactly like routes/explore.tsx's own
// (unchanged, still deep-linkable) rendering.

function countryWordFor(count: number, language: Language): string {
  if (language === "en") return count === 1 ? "country" : "countries";
  return count === 1 ? "país" : "países";
}

function cityWordFor(count: number, language: Language): string {
  if (language === "en") return count === 1 ? "city" : "cities";
  if (language === "pt") return count === 1 ? "cidade" : "cidades";
  return count === 1 ? "ciudad" : "ciudades";
}

function availableCityWordFor(count: number, language: Language): string {
  if (language === "en") return count === 1 ? "city available" : "cities available";
  if (language === "pt") return count === 1 ? "cidade disponível" : "cidades disponíveis";
  return count === 1 ? "ciudad disponible" : "ciudades disponibles";
}

function formatCountryCoverageLabel(country: DestinationCoverageCountry, language: Language): string {
  if (country.activity_count === null) return translate(language, "home.geoCoverage.inventoryToVerify");
  const suffix = country.count_is_complete ? "" : "+";
  return translate(language, "home.geoCoverage.activitiesObserved", { count: country.activity_count, suffix });
}

function HomeGeographicCoverage({
  loading,
  error,
  result,
  onSelectCity,
}: {
  loading:      boolean;
  error:        boolean;
  result:       DestinationExploreResult | null;
  onSelectCity: (iata: string, name: string) => void;
}) {
  const { language, t } = useI18n();

  const summaryCopy = useMemo(() => {
    if (!result || result.summary.activity_count === null) {
      return t("home.geoCoverage.summaryFallback");
    }
    const suffix = result.summary.count_is_complete ? "" : "+";
    const activities = translate(language, "home.geoCoverage.activitiesObserved", { count: result.summary.activity_count, suffix });
    const cities = `${result.summary.observed_city_count} ${cityWordFor(result.summary.observed_city_count, language)}`;
    return t("home.geoCoverage.summaryWithCounts", { activities, cities });
  }, [result, language, t]);

  return (
    <section className="px-5 sm:px-8 py-8 sm:py-10">
      <div style={{ maxWidth: "1040px", margin: "0 auto" }}>
        {loading && (
          <div className="flex min-h-[40vh] items-center justify-center gap-3 text-sm" style={{ color: T.inkMuted }}>
            <Loader2 size={18} className="animate-spin" aria-hidden />
            {t("home.geoCoverage.loading")}
          </div>
        )}

        {!loading && error && (
          <div className="mx-auto max-w-xl py-16 text-center">
            <Globe2 size={28} className="mx-auto mb-4" style={{ color: T.inkMuted }} aria-hidden />
            <h1 className="text-xl font-semibold" style={{ color: T.ink }}>{t("home.geoCoverage.errorTitle")}</h1>
            <p className="mt-2 text-sm" style={{ color: T.inkMuted }}>
              {t("home.geoCoverage.errorBody")}
            </p>
          </div>
        )}

        {!loading && !error && result && (
          <>
            <header className="pb-6 mb-6" style={{ borderBottom: `1px solid ${T.borderSoft}` }}>
              <p className="mb-1.5 text-[10px] font-bold tracking-[0.18em] uppercase" style={{ color: T.inkMuted }}>
                {t("home.geoCoverage.exploreByGeo")}
              </p>
              {/* Deliberately never reads the scope's own representative-
                  airport code here — a country/region/continent must never
                  be labeled with any single city's IATA. */}
              <h1 className="text-2xl sm:text-3xl font-semibold" style={{ color: T.ink, letterSpacing: "-0.01em" }}>
                {result.scope.name}
              </h1>
              <p className="mt-2 max-w-2xl text-sm leading-6" style={{ color: T.inkMuted }}>
                {summaryCopy}
              </p>
              <div className="mt-4 flex flex-wrap gap-x-5 gap-y-1.5 text-[13px]" style={{ color: T.ink }}>
                <span>{result.summary.country_count} {countryWordFor(result.summary.country_count, language)}</span>
                <span>{result.summary.city_count} {availableCityWordFor(result.summary.city_count, language)}</span>
              </div>
            </header>

            <div className="flex flex-col gap-8">
              {result.countries.map((country) => (
                <section key={country.iso2 ?? country.name}>
                  <div className="mb-3 flex flex-col justify-between gap-1 sm:flex-row sm:items-end">
                    <div>
                      <h2 className="text-lg font-semibold" style={{ color: T.ink }}>{country.name}</h2>
                      <p className="mt-0.5 text-xs" style={{ color: T.inkMuted }}>
                        {country.city_count} {cityWordFor(country.city_count, language)} · {formatCountryCoverageLabel(country, language)}
                      </p>
                    </div>
                    {!country.count_is_complete && (
                      <span className="text-xs" style={{ color: T.inkMuted }}>
                        {t("home.geoCoverage.partialDataNote", { observed: country.observed_city_count, total: country.city_count })}
                      </span>
                    )}
                  </div>

                  <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2 lg:grid-cols-3">
                    {country.cities.map((city) => (
                      <button
                        key={city.iata}
                        type="button"
                        onClick={() => onSelectCity(city.iata, city.name)}
                        className="group flex min-h-[44px] items-center gap-3 px-3.5 py-3 text-left transition-colors hover:opacity-90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2"
                        style={{
                          borderRadius: "8px",
                          border: `1px solid ${T.border}`,
                          background: T.parchment,
                          outlineColor: "#E8194B",
                        }}
                      >
                        <span
                          className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full"
                          style={{ background: T.blueFaint, color: T.blue }}
                        >
                          <MapPin size={15} aria-hidden />
                        </span>
                        <span className="min-w-0 flex-1">
                          <span className="flex items-baseline gap-1.5">
                            <span className="truncate text-sm font-semibold" style={{ color: T.ink }}>{city.name}</span>
                            <span className="text-[9px] font-bold" style={{ color: T.inkMuted }}>{city.iata}</span>
                          </span>
                          <span className="mt-0.5 block text-[11px]" style={{ color: T.inkMuted }}>
                            {city.activity_count === null
                              ? t("home.geoCoverage.inventoryToVerify")
                              : translate(language, "home.geoCoverage.activitiesObserved", { count: city.activity_count, suffix: "" })}
                          </span>
                        </span>
                        <ArrowRight size={14} className="shrink-0" style={{ color: T.inkMuted }} aria-hidden />
                      </button>
                    ))}
                  </div>
                </section>
              ))}
            </div>
          </>
        )}
      </div>
    </section>
  );
}

// ─── HowItWorksBand ─────────────────────────────────────────────────────────
// Sprint: Contact + Brand Trust V1, Task 4 — a compact, honest "Cómo
// funciona SiftTrip" band, exact copy from this sprint's own brief (5
// short statements). Rendered unconditionally, right above the footer, in
// every home mode (hero/coverage/results) — never gated on search state,
// never touching MarketplaceShell or any provider/ranking/filter logic.
// Deliberately plain: a numbered list, no imagery, no oversized hero.

function HowItWorksBand() {
  const { t } = useI18n();
  const steps = [
    t("home.howItWorks.step1"),
    t("home.howItWorks.step2"),
    t("home.howItWorks.step3"),
    t("home.howItWorks.step4"),
    t("home.howItWorks.step5"),
  ];
  return (
    <section style={{ borderTop: `1px solid ${T.borderSoft}`, background: "#FFFFFF" }}>
      <div className="mx-auto px-5 sm:px-8 py-8 sm:py-10" style={{ maxWidth: "1040px" }}>
        <p
          className="text-xs font-bold uppercase tracking-[0.18em] mb-4"
          style={{ color: T.inkMuted }}
        >
          {t("home.howItWorks.eyebrow")}
        </p>
        <ol className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-5">
          {steps.map((step, i) => (
            <li key={i} className="flex gap-2.5">
              <span
                aria-hidden
                className="shrink-0 flex items-center justify-center rounded-full text-[11px] font-bold"
                style={{ width: "22px", height: "22px", background: T.blueFaint, color: T.blue }}
              >
                {i + 1}
              </span>
              <p className="text-[13px] leading-snug" style={{ color: T.ink }}>{step}</p>
            </li>
          ))}
        </ol>
      </div>
    </section>
  );
}

// ─── LandingPage ──────────────────────────────────────────────────────────────

type HomeMode = "hero" | "coverage" | "results";

export function dateSelectionFromUrl(search: HomeSearchParams): DateSelection {
  if (search.departure_date) {
    return {
      mode: "exact",
      travelDate: search.departure_date,
      departure_date: search.departure_date,
      return_date: search.return_date ?? null,
    };
  }
  if (search.travel_month) {
    return {
      mode: "flexible",
      travelDate: search.travel_month,
      travel_month: search.travel_month,
      flexible_days: search.flexible_days ?? null,
      flexibility_window: search.flexibility_window ?? null,
    };
  }
  return { mode: "none", travelDate: null };
}

export function travelerFromUrl(search: HomeSearchParams): TravelerState {
  const childrenAges = search.children_ages
    ? search.children_ages.split(",").map(Number).filter(n => Number.isInteger(n) && n >= 0)
    : [];
  return {
    adults: search.adults ?? DEFAULT_TRAVELER.adults,
    childrenAges,
    type: childrenAges.length > 0 ? "familia" : DEFAULT_TRAVELER.type,
  };
}

// ─── Sprint: Search Scope + Locale-Stable Inventory + Traveler Type Label
// Hotfix V1, Parte A — the ONE decision point that routes a submitted
// selection to the marketplace (enterResultsForCity) vs. geographic
// coverage (enterCoverage). Extracted as a pure function so the hybrid
// DestinationPicker's city-vs-scope contract is directly testable without
// a DOM/testing-library harness: a scope candidate (country/continent/
// region) always has `iata: null` (see DestinationPicker.tsx's own
// scopeCandidateToSuggestion — it force-nulls this even when the backend
// entry carries a representative "entry gateway" airport), so this
// condition already naturally excludes it — no entity_type-specific
// scope-detection logic was ever needed beyond the truthy `.iata` check
// that was already here.
export function shouldEnterResultsForCity(selection: SelectedDestination | null): boolean {
  return Boolean(
    selection
    && (selection.entity_type === "city" || selection.entity_type === "airport")
    && selection.iata
  );
}

export function destinationSelectionFromUrl(search: HomeSearchParams): SelectedDestination | null {
  if (!search.destination_iata || !search.destination_name) return null;
  return {
    id: search.destination_iata,
    entity_type: "city",
    iata: search.destination_iata,
    nearest_airport_iata: null,
    name: search.destination_name,
    name_en: search.destination_name,
    country_iso2: null,
    continent_id: null,
    lat: null,
    lng: null,
    planning_tier: "unknown",
    popularity_score: 0,
    transport_nodes: [],
  };
}

export function dateSelectionToUrlParams(dateSelection: DateSelection): Pick<
  HomeSearchParams, "departure_date" | "return_date" | "travel_month" | "flexible_days" | "flexibility_window"
> {
  if (dateSelection.mode === "exact") {
    return {
      departure_date: dateSelection.departure_date ?? undefined,
      return_date: dateSelection.return_date ?? undefined,
      travel_month: undefined, flexible_days: undefined, flexibility_window: undefined,
    };
  }
  if (dateSelection.mode === "flexible") {
    return {
      travel_month: dateSelection.travel_month ?? undefined,
      flexible_days: dateSelection.flexible_days ?? undefined,
      flexibility_window: dateSelection.flexibility_window ?? undefined,
      departure_date: undefined, return_date: undefined,
    };
  }
  return {
    departure_date: undefined, return_date: undefined,
    travel_month: undefined, flexible_days: undefined, flexibility_window: undefined,
  };
}

// ─── Unified Marketplace Shell Behavioral Parity V1.1, Parte 2 ─────────────
// Pure URL <-> state helpers, used both for the initial useState()
// initializers AND the sync effect below that reacts to Route.useSearch()
// changing from Back/Forward (never just at mount). Keeping mode derivation
// (homeModeFromSearch) separate from the full state bundle
// (homeStateFromSearch) lets each be tested/reasoned about independently.

export function homeModeFromSearch(search: HomeSearchParams): HomeMode {
  if (search.destination_iata) return "results";
  if (search.scope) return "coverage";
  return "hero";
}

export interface HomeStateFromSearch {
  mode:             HomeMode;
  destination:      SelectedDestination | null;
  destinationValue: string;
  query:            string;
  dateSelection:    DateSelection;
  traveler:         TravelerState;
}

export function homeStateFromSearch(search: HomeSearchParams): HomeStateFromSearch {
  const destination = destinationSelectionFromUrl(search);
  return {
    mode:             homeModeFromSearch(search),
    destination,
    destinationValue: destination?.name ?? "",
    query:            search.q ?? "",
    dateSelection:    dateSelectionFromUrl(search),
    traveler:         travelerFromUrl(search),
  };
}

/** Stable, dependency-array-friendly key for "did the URL's own search
 *  state actually change" — avoids depending on Route.useSearch()'s object
 *  reference, which this repo makes no guarantee is stable across renders
 *  when the resolved values are unchanged. */
export function homeSearchKey(search: HomeSearchParams): string {
  return JSON.stringify([
    search.destination_iata, search.destination_name, search.q,
    search.departure_date, search.return_date, search.travel_month,
    search.flexible_days, search.flexibility_window, search.adults,
    search.children_ages, search.scope,
  ]);
}

// ─── Unified Marketplace Shell Behavioral Parity V1.1, Parte 3 ────────────
// The exact race-guard logic behind the coverage-loading effect, extracted
// so it's directly testable without a renderer: a requestId minted here
// and compared on every settle path is the ONLY thing that decides whether
// a response is still current — a late-arriving response for a scope the
// user has already moved on from (e.g. España resolving AFTER the user
// already switched to Colombia) is silently discarded, never applied.
export async function fetchCoverageForScope(
  scope:        string,
  requestIdRef: { current: number },
  handlers: {
    onSuccess: (result: DestinationExploreResult) => void;
    onError:   () => void;
    onSettle:  () => void;
  },
  fetchFn: (scope: string) => Promise<DestinationExploreResult> = exploreDestinations,
): Promise<void> {
  const requestId = ++requestIdRef.current;
  try {
    const result = await fetchFn(scope);
    if (requestIdRef.current !== requestId) return; // superseded — discard
    handlers.onSuccess(result);
  } catch {
    if (requestIdRef.current !== requestId) return;
    handlers.onError();
  } finally {
    if (requestIdRef.current === requestId) handlers.onSettle();
  }
}

// ─── Unified Marketplace Shell Behavioral Parity V1.1, Parte 5 ────────────
// Deterministic ranking-context derivation — parseQuery() already exists
// and already only ever returns canonical, pre-existing category tags
// (never invented, never the free text itself). It requires a recognized
// city/region token to return anything at all (see its own module doc),
// which is exactly why the destination name is always prefixed here —
// intent text alone ("historia") would otherwise never resolve. Never
// calls an LLM, /parse/intent, or /experiences/sequence.
export function deriveMarketplaceInterests(destinationName: string, intentText: string): string[] {
  const parsed = parseQuery(`${destinationName} ${intentText}`.trim());
  return parsed?.interests ?? [];
}

function LandingPage() {
  const navigate = useNavigate();
  const urlSearch = Route.useSearch();

  const [destinationValue, setDestinationValue] = useState(() => homeStateFromSearch(urlSearch).destinationValue);
  const [selectedDestination, setSelectedDestination] = useState<SelectedDestination | null>(
    () => homeStateFromSearch(urlSearch).destination,
  );
  const [query,          setQuery]          = useState(() => homeStateFromSearch(urlSearch).query);
  const [dateSelection,  setDateSelection]  = useState<DateSelection>(() => homeStateFromSearch(urlSearch).dateSelection);
  const [traveler,       setTraveler]       = useState<TravelerState>(() => homeStateFromSearch(urlSearch).traveler);
  const [placeholderIdx, setPlaceholderIdx] = useState(0);
  const [loading,        setLoading]        = useState(false);
  const { startLoading }                    = useRheaLoading();
  const { t: tHome }                        = useI18n();

  // Sprint: Search Scope + Locale-Stable Inventory + Traveler Type Label
  // Hotfix V1, Parte C — a phrase typed in the query field (e.g. "Madrid
  // en pareja") updates the visible traveler chip, but only while the
  // user hasn't made an EXPLICIT popover choice (traveler.explicitlyChosen)
  // — an intentional click always outranks a looser text match, even if
  // the user keeps typing afterward.
  useEffect(() => {
    if (traveler.explicitlyChosen) return;
    const detected = matchTravelerTypeFromQuery(query);
    if (!detected || detected === traveler.type) return;
    setTraveler((prev) => {
      if (prev.explicitlyChosen) return prev;
      const opt = TRAVELER_OPTIONS.find((o) => o.value === detected)!;
      return {
        adults: detected === "solo" ? 1 : Math.max(prev.adults, opt.adultDefault),
        childrenAges: detected === "solo" ? [] : prev.childrenAges,
        type: detected,
        manuallySet: true,
      };
    });
  }, [query, traveler.explicitlyChosen, traveler.type]);

  // Unified Marketplace Shell + Direct Structured Search V1 — "hero" is the
  // full editorial landing; "results" hosts MarketplaceShell inline (a
  // structured city/airport search, calling POST /experiences/search
  // directly — never /parse/intent, never /experiences/sequence); "coverage"
  // shows a country/region/continent's geographic coverage inline, never
  // navigating to a separate route. Restored from the URL on first render so
  // a deep link / refresh lands back in the same mode.
  const [mode, setMode] = useState<HomeMode>(() => homeModeFromSearch(urlSearch));
  const [coverage, setCoverage]               = useState<DestinationExploreResult | null>(null);
  const [coverageLoading, setCoverageLoading] = useState(false);
  const [coverageError, setCoverageError]     = useState(false);

  useEffect(() => {
    const id = setInterval(
      () => setPlaceholderIdx(i => (i + 1) % PLACEHOLDERS.length),
      3500,
    );
    return () => clearInterval(id);
  }, []);

  // Unified Marketplace Shell Behavioral Parity V1.1, Parte 2 — syncs mode/
  // destination/query/dates/travelers whenever Route.useSearch() changes
  // for a reason OTHER than the initial mount (Back/Forward navigation, or
  // any other external URL change) — the mount case is already covered by
  // the useState() initializers above via the SAME homeStateFromSearch().
  // Never calls navigate() itself (only local setters), so this can never
  // become a navigate -> effect -> navigate loop; it only ever REACTS to a
  // URL that has already changed.
  const urlKey = homeSearchKey(urlSearch);
  const isFirstRenderRef = useRef(true);
  useEffect(() => {
    if (isFirstRenderRef.current) {
      isFirstRenderRef.current = false;
      return;
    }
    const next = homeStateFromSearch(urlSearch);
    setMode(next.mode);
    setSelectedDestination(next.destination);
    setDestinationValue(next.destinationValue);
    setQuery(next.query);
    setDateSelection(next.dateSelection);
    setTraveler(next.traveler);
    if (next.mode !== "coverage") {
      // Back to hero (empty URL) or straight to results — either way,
      // any previously-loaded coverage view is no longer relevant.
      setCoverage(null);
      setCoverageError(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlKey]);

  // Unified Marketplace Shell Behavioral Parity V1.1, Parte 3 — the ONE
  // effect that ever loads coverage data, keyed on urlSearch.scope alone.
  // Covers every case that used to need separate handling: initial mount
  // with a scope already in the URL (deep link/refresh), a NEW scope from
  // enterCoverage() below (which only ever updates the URL, never fetches
  // itself), and Back/Forward changing the scope. coverageRequestIdRef
  // discards a stale/out-of-order response (e.g. España's request resolves
  // AFTER the user has already moved on to Colombia) — never overwrites
  // whatever the CURRENT scope's own latest request produced.
  const coverageRequestIdRef = useRef(0);
  useEffect(() => {
    const scope = urlSearch.scope;
    if (!scope) return;
    setCoverage(null);
    setCoverageLoading(true);
    setCoverageError(false);
    void fetchCoverageForScope(scope, coverageRequestIdRef, {
      onSuccess: setCoverage,
      onError:   () => setCoverageError(true),
      onSettle:  () => setCoverageLoading(false),
    });
  }, [urlSearch.scope]);

  // Unified Marketplace Shell + Direct Structured Search V1, action 1/2,
  // extended by Behavioral Parity V1.1, Parte 5 — the provider-agnostic
  // search context MarketplaceShell/useMarketplaceSearch consume. Built
  // from LOCAL state only, never from a /parse/intent or
  // /experiences/sequence response. interests[] is derived exclusively via
  // the existing, deterministic parseQuery() over "destination + intent" —
  // never an LLM call, never invented categories, never free text
  // persisted anywhere analytics can see. parseQuery() requires a
  // recognized city/region token to return anything at all (see its own
  // module doc), which is exactly why the destination name is prefixed
  // here — intent text alone ("historia") would otherwise never resolve.
  const searchContext: MarketplaceSearchContext | null = useMemo(() => {
    if (mode !== "results" || !selectedDestination?.iata) return null;
    const trimmedIntent = query.trim();
    return {
      city:        selectedDestination.name,
      cityIata:    selectedDestination.iata,
      cityDisplay: selectedDestination.name,
      intent:      trimmedIntent || undefined,
      dates:       { mode: dateSelection.mode, travelDate: dateSelection.travelDate },
      adults:      traveler.adults,
      childrenAges: traveler.childrenAges,
      interests:   deriveMarketplaceInterests(selectedDestination.name, trimmedIntent),
      // Sprint: Destination Marketplace Identity vs Nearest Airport
      // Separation V1, Parte E — undefined when selectedDestination came
      // from a plain iata/name pair (URL restoration, teaser/coverage
      // picks) — buildMarketplaceSearchParams treats that exactly like a
      // request that never had these fields.
      destinationId:            selectedDestination.destination_id,
      marketplaceSearchName:    selectedDestination.marketplace_search_name,
      destinationIdentityKind:  selectedDestination.destination_identity_kind,
    };
  }, [mode, selectedDestination, query, dateSelection, traveler]);

  // Unified Marketplace Funnel & Request Lifecycle V1.2, Parte 3 — page
  // engagement is tracked only while Home is actually showing results for a
  // real destination. The existing mechanism (initPageEngagementTracking)
  // already records duration/had_results/provider_clicked/
  // comparison_opened via the far-apart call sites that already call
  // logMarketplaceEvent (ComparisonDrawer, ExperienceDetailDrawer, etc.) —
  // never duplicated here. Keyed on the destination's own IATA (not the
  // full search context) so an intent/date/traveler tweak within the SAME
  // destination doesn't reset engagement tracking — only leaving results,
  // switching destination, or unmounting runs the cleanup (which fires
  // exactly once, per initPageEngagementTracking's own guard).
  useEffect(() => {
    if (mode !== "results" || !selectedDestination?.iata) return;
    return initPageEngagementTracking();
  }, [mode, selectedDestination?.iata]);

  // Unified Marketplace Funnel & Request Lifecycle V1.2, Parte 2 — this is
  // the ONLY call site that starts a city-level results search (submit
  // principal, a suggested city, and a coverage city pick all route through
  // here — see handleSubmit/handleSelectCity/handleCoverageCitySelect
  // below), so firing experiences_search_submitted here — never from the
  // URL-sync effect, Back/Forward, retry, filters, warmup, or pagination —
  // guarantees exactly one event per genuine human action. Payload never
  // carries the raw query, only query_present.
  const enterResultsForCity = useCallback((
    iata: string,
    name: string,
    intentText: string,
    sourceSurface: "search_bar" | "teaser" = "search_bar",
    // Sprint: Destination Marketplace Identity vs Nearest Airport
    // Separation V1, Parte E — optional, additive. Only the picker's own
    // handleSubmit path has these (see its call site below); a suggested-
    // city teaser or coverage-view pick has no DestinationCandidate to
    // draw them from, so they stay undefined there — exactly the old
    // behavior for those two flows.
    identity?: {
      destinationId?: string;
      marketplaceSearchName?: string;
      destinationIdentityKind?: string;
      nearestAirportIata?: string | null;
    },
  ) => {
    setSelectedDestination({
      id: iata, entity_type: "city", iata,
      nearest_airport_iata: identity?.nearestAirportIata ?? null,
      name, name_en: name, country_iso2: null, continent_id: null,
      lat: null, lng: null, planning_tier: "unknown", popularity_score: 0, transport_nodes: [],
      destination_id: identity?.destinationId,
      marketplace_search_name: identity?.marketplaceSearchName,
      destination_identity_kind: identity?.destinationIdentityKind,
    });
    setDestinationValue(name);
    setQuery(intentText);
    setMode("results");

    track("experiences_search_submitted", {
      query_present:    intentText.trim().length > 0,
      destination_iata: iata,
      destination_name: name,
      adults:           traveler.adults,
      has_dates:        dateSelection.mode !== "none",
      source_surface:   sourceSurface,
    });
    logMarketplaceEvent("experiences_search_submitted", {
      destination_iata: iata,
    });

    void navigate({
      to: "/",
      search: {
        destination_iata: iata,
        destination_name: name,
        q: intentText.trim() || undefined,
        ...dateSelectionToUrlParams(dateSelection),
        adults: traveler.adults !== DEFAULT_TRAVELER.adults ? traveler.adults : undefined,
        children_ages: traveler.childrenAges.length > 0 ? traveler.childrenAges.join(",") : undefined,
        scope: undefined,
      },
    });
  }, [dateSelection, traveler, navigate]);

  // Unified Marketplace Shell Behavioral Parity V1.1, Parte 3 — only ever
  // updates the URL; the single effect above (keyed on urlSearch.scope) is
  // the ONLY place that actually calls exploreDestinations(), so a scope
  // change is never fetched twice.
  const enterCoverage = useCallback((scopeName: string, intentText: string) => {
    setMode("coverage");
    void navigate({
      to: "/",
      search: {
        scope: scopeName,
        q: intentText.trim() || undefined,
        ...dateSelectionToUrlParams(dateSelection),
        adults: traveler.adults !== DEFAULT_TRAVELER.adults ? traveler.adults : undefined,
        children_ages: traveler.childrenAges.length > 0 ? traveler.childrenAges.join(",") : undefined,
        destination_iata: undefined,
        destination_name: undefined,
      },
    });
  }, [dateSelection, traveler, navigate]);

  // Unified Marketplace Shell + Direct Structured Search V1, action 5 — a
  // known city/airport stays on this page, updates the URL, and calls
  // POST /experiences/search directly via MarketplaceShell/useMarketplaceSearch.
  // Action 6 — a country/region/continent shows geographic coverage in the
  // same shell instead; the scope is never associated with a single city's
  // IATA (see HomeGeographicCoverage's own doc).
  const handleSubmit = useCallback(async () => {
    if (!selectedDestination) return;
    const trimmed = query.trim();
    setLoading(true);
    const finish = startLoading(tHome("home.loadingSearch"));
    try {
      await waitForNextPaint();
      if (shouldEnterResultsForCity(selectedDestination)) {
        enterResultsForCity(selectedDestination.iata!, selectedDestination.name, trimmed, "search_bar", {
          destinationId: selectedDestination.destination_id,
          marketplaceSearchName: selectedDestination.marketplace_search_name,
          destinationIdentityKind: selectedDestination.destination_identity_kind,
          nearestAirportIata: selectedDestination.nearest_airport_iata,
        });
      } else {
        enterCoverage(selectedDestination.name, trimmed);
      }
    } finally {
      setLoading(false);
      finish();
    }
  }, [selectedDestination, query, startLoading, enterResultsForCity, enterCoverage, tHome]);

  const handleSelectCity = useCallback(async (display: string, iata: string) => {
    setLoading(true);
    const finish = startLoading(tHome("home.loadingSearch"));
    try {
      await waitForNextPaint();
      enterResultsForCity(iata, display, "", "teaser");
    } finally {
      setLoading(false);
      finish();
    }
  }, [startLoading, enterResultsForCity, tHome]);

  // Action 6 — choosing a city from the coverage view switches directly to
  // results, never a second round trip through a separate route.
  const handleCoverageCitySelect = useCallback((iata: string, name: string) => {
    enterResultsForCity(iata, name, query.trim(), "teaser");
  }, [enterResultsForCity, query]);

  const handleNewSearch = useCallback(() => {
    setMode("hero");
    setSelectedDestination(null);
    setDestinationValue("");
    setQuery("");
    setCoverage(null);
    setCoverageError(false);
    void navigate({ to: "/", search: {} });
  }, [navigate]);

  const compactLabel = mode === "results"
    ? (selectedDestination?.name ?? "")
    : (urlSearch.scope ?? "");

  return (
    <div
      className="relative min-h-screen flex flex-col"
      style={{ background: T.cream }}
    >
      <RheaPrototypeHeader mode="home" />

      {/* Full-Width Marketplace Grid & Scalable Provider Filter V1, Parte A
          — no sidebar, no bottom mobile nav: Home is the marketplace
          itself, not one tab among several unbuilt verticals. `main` runs
          the full viewport width; each mode below owns its own internal
          max-width container (hero/coverage keep their existing ~1040px
          editorial width, results gets the wider ~1600px grid container
          below). */}
      <main className="flex-1 flex flex-col min-w-0 pt-16 w-full">
        {mode === "hero" ? (
          <HomeHero
            destinationValue={destinationValue}
            onDestinationChange={setDestinationValue}
            selectedDestination={selectedDestination}
            onDestinationSelect={setSelectedDestination}
            query={query}
            onQueryChange={setQuery}
            onSubmit={handleSubmit}
            loading={loading}
            dateSelection={dateSelection}
            onDateChange={setDateSelection}
            traveler={traveler}
            onTravelerChange={setTraveler}
            placeholderIdx={placeholderIdx}
          />
        ) : (
          <CompactSearchBar label={compactLabel} onEdit={handleNewSearch} />
        )}

        {mode === "hero" && (
          <>
            <TrustBand />
            <HomeSuggestions onSelectCity={handleSelectCity} />
          </>
        )}

        {mode === "coverage" && (
          <HomeGeographicCoverage
            loading={coverageLoading}
            error={coverageError}
            result={coverage}
            onSelectCity={handleCoverageCitySelect}
          />
        )}

        {mode === "results" && (
          <div className="mx-auto w-full px-4 sm:px-6 lg:px-8 py-6" style={{ maxWidth: "1600px" }}>
            {/* Marketplace Grid Behavioral Correctness V1.1, Parte B — key
                on the logical-search identity (destination/intent/dates/
                travelers, never pagination/filters — see
                marketplaceSearchContextKey's own doc) so a NEW destination
                mounts a genuinely fresh MarketplaceShell instance,
                synchronously, before any effect can run a transient
                request carrying the PREVIOUS destination's filters (e.g. a
                GuruWalk selection made in Madrid reaching Bogota's very
                first request). A filters-only change, "cargar más", or
                warmup never touches this key. */}
            <MarketplaceShell
              key={marketplaceSearchContextKey(searchContext)}
              context={searchContext}
              onNewSearch={handleNewSearch}
            />
          </div>
        )}

        <HowItWorksBand />
        <SiteFooter />
      </main>
    </div>
  );
}
